import { DependencyList, useEffect, useRef, useState } from 'react';
import { EntityName } from 'record-it-time-common/EntityName';
import { useEntitiesChangedListener } from '../useEntitiesChangedListener/useEntitiesChangedListener';

/**
 * An extended version of `useMemo` that also recomputes the value if a Repository
 * of the given entityNames emits updates.
 *
 * @example
 * let userId: string|null = null;
 * const userRepository = useRepository(EntityName.User);
 *
 * // Retrieve the user if `userId` is available
 * const user = useSynchroMemo(() => {
 *   return userId ? userRepository.getById(userId) : null;
 * }, [userId, userRepository], [EntityName.User]);
 */
export function useSynchroMemo<T>(
  factory: () => T,
  deps: DependencyList,
  entityNames: EntityName[]
): T {
  const [state, setState] = useState<T>(factory);
  const isFirstRenderRef = useRef(true);

  // Recompute value if dependencies change (if it's not the first render)
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
    } else {
      setState(factory());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [factory, ...deps]);

  // Recompute value if entities change
  useEntitiesChangedListener(entityNames, () => setState(factory()));

  return state;
}
